import { useState, useEffect } from 'react';
import { Location } from '../types/locations';

const STORAGE_KEY = 'userLocation';
const STORAGE_KEY_FULL = 'userLocationFull';

/**
 * Stored location data - subset of Location with fields needed for coupons
 */
export interface StoredLocation {
  LocationTitle: string;
  LocationCouponID?: string;
  LocationCoupons: boolean;
}

/**
 * Hook to manage user's selected location in localStorage
 * Stores both the location title (for backwards compatibility) and the full location data
 */
export function useUserLocation() {
  const [selectedLocationTitle, setSelectedLocationTitle] = useState<string | null>(null);
  const [selectedLocationData, setSelectedLocationData] = useState<StoredLocation | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const loadFromStorage = () => {
      const storedTitle = localStorage.getItem(STORAGE_KEY);
      setSelectedLocationTitle(storedTitle);

      const storedFull = localStorage.getItem(STORAGE_KEY_FULL);
      if (storedFull) {
        try {
          setSelectedLocationData(JSON.parse(storedFull));
        } catch {
          setSelectedLocationData(null);
        }
      } else {
        setSelectedLocationData(null);
      }
    };

    // Load initial value
    loadFromStorage();

    // Listen for storage changes (when localStorage is updated from other tabs/components)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === STORAGE_KEY_FULL) {
        loadFromStorage();
      }
    };

    // Listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      loadFromStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLocationChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLocationChanged', handleCustomStorageChange);
    };
  }, []);

  /**
   * Set the selected location - accepts either a Location object or just the title
   * When passing a Location object, the LocationCouponID will be stored for coupon API calls
   */
  const setLocation = (location: Location | string) => {
    if (typeof location === 'string') {
      // Legacy support - just store the title
      localStorage.setItem(STORAGE_KEY, location);
      setSelectedLocationTitle(location);
      // Clear full data since we don't have it
      localStorage.removeItem(STORAGE_KEY_FULL);
      setSelectedLocationData(null);
    } else {
      // Store both title and full location data
      localStorage.setItem(STORAGE_KEY, location.LocationTitle);
      setSelectedLocationTitle(location.LocationTitle);

      const storedData: StoredLocation = {
        LocationTitle: location.LocationTitle,
        LocationCouponID: location.LocationCouponID,
        LocationCoupons: location.LocationCoupons,
      };
      localStorage.setItem(STORAGE_KEY_FULL, JSON.stringify(storedData));
      setSelectedLocationData(storedData);
    }
    // Dispatch custom event to notify other components in the same tab
    window.dispatchEvent(new Event('userLocationChanged'));
  };

  const clearLocation = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_FULL);
    setSelectedLocationTitle(null);
    setSelectedLocationData(null);
    // Dispatch custom event to notify other components in the same tab
    window.dispatchEvent(new Event('userLocationChanged'));
  };

  const isSelected = (locationTitle: string) => {
    return selectedLocationTitle === locationTitle;
  };

  return {
    /** The selected location title (for backwards compatibility) */
    selectedLocation: selectedLocationTitle,
    /** The full stored location data including LocationCouponID */
    selectedLocationData,
    /** The branch_id (LocationCouponID) for the coupon API */
    branchId: selectedLocationData?.LocationCouponID || null,
    /** Whether the selected location has coupons enabled */
    hasCoupons: selectedLocationData?.LocationCoupons ?? false,
    setLocation,
    clearLocation,
    isSelected,
  };
}





